"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { toast } from "sonner"
import api from "@/lib/api"
import { selectedBranchId } from "@/lib/branch-selection"
import { useAuth } from "@/lib/auth-context"
import {
  formatWorkspaceNotification,
  playWorkspaceNotificationBeep,
  type WorkspaceNotificationRow,
} from "@/lib/workspace-notifications"

const POLL_MS = 20_000
const PULSE_MS = 5_000

type WorkspaceNotificationsContextValue = {
  enabled: boolean
  unreadCount: number
  pulseNav: boolean
  notifications: WorkspaceNotificationRow[]
  loading: boolean
  refresh: (opts?: { unreadOnly?: boolean; updateList?: boolean }) => Promise<void>
  markRead: (id: number) => Promise<void>
  markAllRead: () => Promise<void>
}

const WorkspaceNotificationsContext = createContext<WorkspaceNotificationsContextValue | null>(null)

export function WorkspaceNotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const enabled = user?.features?.multiBranch === true
  const [unreadCount, setUnreadCount] = useState(0)
  const [pulseNav, setPulseNav] = useState(false)
  const [notifications, setNotifications] = useState<WorkspaceNotificationRow[]>([])
  const [loading, setLoading] = useState(false)
  const announcedRef = useRef(new Set<number>())
  const branchKeyRef = useRef<string | null>(null)
  const pulseTimerRef = useRef<number | null>(null)

  const triggerPulse = useCallback(() => {
    setPulseNav(true)
    if (pulseTimerRef.current != null) window.clearTimeout(pulseTimerRef.current)
    pulseTimerRef.current = window.setTimeout(() => {
      setPulseNav(false)
      pulseTimerRef.current = null
    }, PULSE_MS)
  }, [])

  const refresh = useCallback(async (opts?: { unreadOnly?: boolean; updateList?: boolean }) => {
    if (!enabled) return
    const branchId = selectedBranchId()
    if (!branchId) return
    if (branchKeyRef.current !== branchId) {
      branchKeyRef.current = branchId
      announcedRef.current = new Set()
      setUnreadCount(0)
      setNotifications([])
    }
    const updateList = opts?.updateList !== false
    if (updateList) setLoading(true)
    try {
      const { data } = await api.get<{
        notifications: WorkspaceNotificationRow[]
        unreadCount: number
      }>("/api/notifications", {
        params: opts?.unreadOnly ? { unreadOnly: "1" } : undefined,
        headers: { "X-Branch-Id": branchId },
      })
      if (updateList) setNotifications(data.notifications)
      setUnreadCount(data.unreadCount)
      if (opts?.unreadOnly) {
        for (const row of data.notifications) {
          if (announcedRef.current.has(row.id)) continue
          announcedRef.current.add(row.id)
          const { title, description } = formatWorkspaceNotification(row)
          toast.info(title, { description, duration: 8000 })
          playWorkspaceNotificationBeep()
          triggerPulse()
        }
      }
    } catch {
      /* Ignore transient network errors during polling. */
    } finally {
      if (updateList) setLoading(false)
    }
  }, [enabled, triggerPulse])

  const markRead = useCallback(async (id: number) => {
    const branchId = selectedBranchId()
    if (!branchId) return
    await api.post(`/api/notifications/${id}/read`, {}, { headers: { "X-Branch-Id": branchId } })
    setNotifications((prev) =>
      prev.map((row) => (row.id === id ? { ...row, readAt: new Date().toISOString() } : row)),
    )
    setUnreadCount((count) => Math.max(0, count - 1))
  }, [])

  const markAllRead = useCallback(async () => {
    const branchId = selectedBranchId()
    if (!branchId) return
    await api.post("/api/notifications/read-all", {}, { headers: { "X-Branch-Id": branchId } })
    const readAt = new Date().toISOString()
    setNotifications((prev) => prev.map((row) => (row.readAt ? row : { ...row, readAt })))
    setUnreadCount(0)
  }, [])

  useEffect(() => {
    if (!enabled) return
    let active = true
    const poll = () => {
      if (!active) return
      void refresh({ unreadOnly: true, updateList: false })
    }
    void poll()
    const timer = window.setInterval(poll, POLL_MS)
    return () => {
      active = false
      window.clearInterval(timer)
      if (pulseTimerRef.current != null) window.clearTimeout(pulseTimerRef.current)
    }
  }, [enabled, refresh])

  const value = useMemo(
    () => ({
      enabled,
      unreadCount,
      pulseNav,
      notifications,
      loading,
      refresh,
      markRead,
      markAllRead,
    }),
    [enabled, unreadCount, pulseNav, notifications, loading, refresh, markRead, markAllRead],
  )

  return (
    <WorkspaceNotificationsContext.Provider value={value}>
      {children}
    </WorkspaceNotificationsContext.Provider>
  )
}

export function useWorkspaceNotifications() {
  const ctx = useContext(WorkspaceNotificationsContext)
  if (!ctx) throw new Error("useWorkspaceNotifications requires WorkspaceNotificationsProvider")
  return ctx
}
